<template>
  <div class="speaker-manager">
    <div class="header">
      <div>
        <h2>
          <i class="fas fa-chalkboard-teacher"></i>
          Speakers
        </h2>
        <p class="subtitle">
          Speakers receive complimentary unlimited CE access and feed both the
          live-event speaker drawdown and the public speaker showcase.
        </p>
      </div>
      <button
        type="button"
        class="btn btn-primary invite-speaker-btn"
        data-testid="invite-speaker-btn"
        @click="openInviteModal()"
      >
        <i class="fas fa-plus"></i>
        Invite Speaker
      </button>
    </div>

    <div v-if="metrics" class="metrics-grid">
      <div class="metric">
        <div class="metric-label">Active speakers</div>
        <div class="metric-value">{{ metrics.speakers.active }} <span class="muted">/ {{ metrics.speakers.total }}</span></div>
        <div class="metric-foot">{{ metrics.speakers.with_courses }} authoring courses</div>
      </div>
      <div class="metric">
        <div class="metric-label">Complimentary CE enrollments</div>
        <div class="metric-value">{{ metrics.complimentary_enrollments }}</div>
        <div class="metric-foot">SPEAKER reason</div>
      </div>
      <div class="metric">
        <div class="metric-label">RACE pass rate</div>
        <div class="metric-value">
          {{ metrics.race.pass_rate != null ? Math.round(metrics.race.pass_rate * 100) + '%' : '—' }}
        </div>
        <div class="metric-foot">
          {{ metrics.race.approved }} approved · {{ metrics.race.requires_resubmission }} need resubmit
        </div>
      </div>
      <div class="metric">
        <div class="metric-label">Upcoming live CE</div>
        <div class="metric-value">{{ metrics.live_events.upcoming }}</div>
        <div class="metric-foot">
          {{ metrics.rsvps.confirmed }} RSVPs · {{ metrics.pending_speaker_applications }} pending applications
        </div>
      </div>
    </div>

    <div class="toolbar">
      <input
        v-model="search"
        type="search"
        placeholder="Search by name, email, credentials, or topic"
        class="search-input"
        @input="debouncedReload"
      />
      <label class="toggle">
        <input v-model="includeInactive" type="checkbox" @change="reload" />
        Show inactive
      </label>
    </div>

    <div v-if="loading" class="loading">Loading speakers…</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else-if="!speakers.length" class="empty">
      No speakers yet. Click <strong>Invite Speaker</strong> above to add the first one.
    </div>
    <table v-else class="speakers-table">
      <thead>
        <tr>
          <th></th>
          <th>Name</th>
          <th>Credentials</th>
          <th>Topics</th>
          <th>Public Slug</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="s in speakers" :key="s.id">
          <td class="avatar-cell">
            <img v-if="s.headshot_url" :src="s.headshot_url" :alt="s.full_name" />
            <span v-else class="avatar-fallback">{{ initials(s) }}</span>
          </td>
          <td>
            <div class="name">{{ s.full_name }}</div>
            <div class="email">{{ s.email }}</div>
          </td>
          <td>{{ s.credentials || '—' }}</td>
          <td>
            <span v-for="t in (s.topics || [])" :key="t" class="topic-pill">{{ t }}</span>
            <span v-if="!s.topics?.length" class="muted">—</span>
          </td>
          <td>
            <code v-if="s.public_slug">/speakers/{{ s.public_slug }}</code>
            <span v-else class="muted">not set</span>
          </td>
          <td>
            <span :class="['status-badge', s.is_active_speaker ? 'active' : 'inactive']">
              {{ s.is_active_speaker ? 'Active' : 'Inactive' }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="pageCount > 1" class="pagination">
      <button :disabled="page <= 1" @click="goPage(page - 1)">Previous</button>
      <span>Page {{ page }} of {{ pageCount }} ({{ total }} speakers)</span>
      <button :disabled="page >= pageCount" @click="goPage(page + 1)">Next</button>
    </div>

    <!-- Invite modal -->
    <div v-if="showInvite" class="modal-overlay" @click.self="closeInviteModal">
      <div class="modal" @click.stop>
        <!-- Post-create credentials view: shown after a successful invite
             that created a new user so the admin can share login info
             out-of-band when SES delivery is slow/sandboxed. -->
        <div v-if="inviteCredentials" class="credentials-panel">
          <h3>Speaker invited</h3>
          <p class="muted">
            <span v-if="inviteCredentials.emailSent">
              An invitation email was sent to
              <strong>{{ inviteCredentials.email }}</strong>. If it doesn't
              arrive within a few minutes (SES sandbox mode, spam, etc.),
              you can share these credentials directly:
            </span>
            <span v-else>
              Share these credentials with the speaker directly:
            </span>
          </p>
          <div class="cred-row">
            <label>Email</label>
            <div class="cred-value">
              <code>{{ inviteCredentials.email }}</code>
              <button
                type="button"
                class="btn copy-btn"
                @click="copyCredential('email', inviteCredentials.email)"
              >
                {{ copiedField === 'email' ? 'Copied' : 'Copy' }}
              </button>
            </div>
          </div>
          <div class="cred-row">
            <label>Temporary password</label>
            <div class="cred-value">
              <code>{{ inviteCredentials.password }}</code>
              <button
                type="button"
                class="btn copy-btn"
                @click="copyCredential('password', inviteCredentials.password)"
              >
                {{ copiedField === 'password' ? 'Copied' : 'Copy' }}
              </button>
            </div>
          </div>
          <p class="muted small">
            They'll be prompted to change this password after logging in.
          </p>
          <div class="modal-actions">
            <button
              type="button"
              class="btn btn-primary"
              @click="closeInviteModal"
            >
              Done
            </button>
          </div>
        </div>
        <template v-else>
        <h3>Invite Speaker</h3>
        <p class="muted">
          Creates a SPEAKER user, populates their profile, and sends a
          welcome email with login credentials.
        </p>
        <!-- novalidate: HTML5 type=email/url on optional fields was silently
             blocking the submit (no console, no network, no backend log) when
             users pasted partial URLs like "linkedin.com/in/jane". We do our
             own validation in submitInvite() so the user always sees why. -->
        <form novalidate @submit.prevent="submitInvite">
          <!-- Org picker: only rendered when the caller has more than one
               choice (i.e. platform admins). Defaults to the recommended
               CE-publishing org so a Compliance admin inviting from a
               non-LMS org doesn't silently create a speaker who sees zero
               courses. -->
          <div v-if="targetOrgs.length > 1" class="form-row">
            <label>
              Speaker organization <span class="required">*</span>
              <span class="muted small">
                — speakers see and enroll in courses owned by this org
              </span>
            </label>
            <select v-model="invite.organization_slug">
              <option
                v-for="o in targetOrgs"
                :key="o.slug"
                :value="o.slug"
              >
                {{ o.name }}{{ o.published_courses ? ` — ${o.published_courses} published courses` : ' — no published courses' }}{{ o.slug === recommendedOrgSlug ? ' ★ recommended' : '' }}
              </option>
            </select>
          </div>
          <div v-else-if="targetOrgs.length === 1" class="form-row">
            <label>Speaker organization</label>
            <div class="muted">
              <i class="fas fa-building"></i>
              {{ targetOrgs[0].name }}
              <span v-if="!targetOrgs[0].is_publishing_lms" class="warning-inline">
                — heads up, this org has no published CE courses yet
              </span>
            </div>
          </div>
          <div class="form-row">
            <label>Email <span class="required">*</span></label>
            <input v-model="invite.email" type="email" autocomplete="email" />
          </div>
          <div class="form-row split">
            <div>
              <label>First name</label>
              <input v-model="invite.first_name" type="text" autocomplete="given-name" />
            </div>
            <div>
              <label>Last name</label>
              <input v-model="invite.last_name" type="text" autocomplete="family-name" />
            </div>
          </div>
          <div class="form-row">
            <label>Credentials</label>
            <input
              v-model="invite.credentials"
              type="text"
              placeholder="DVM, DACVIM"
            />
          </div>
          <div class="form-row">
            <label>Topics (comma-separated)</label>
            <input
              v-model="invite.topicsRaw"
              type="text"
              placeholder="cardiology, internal_medicine"
            />
          </div>
          <div class="form-row">
            <label>Headshot URL</label>
            <input
              v-model.trim="invite.headshot_url"
              type="text"
              inputmode="url"
              placeholder="https://…"
            />
          </div>
          <div class="form-row">
            <label>CV URL</label>
            <input
              v-model.trim="invite.cv_url"
              type="text"
              inputmode="url"
              placeholder="https://…"
            />
          </div>
          <div class="form-row">
            <label>Bio</label>
            <textarea v-model="invite.bio" rows="3" />
          </div>
          <div class="form-row">
            <label class="toggle">
              <input v-model="invite.send_email" type="checkbox" />
              Send welcome email with temporary password
            </label>
          </div>
          <div v-if="inviteError" class="error">{{ inviteError }}</div>
          <div class="modal-actions">
            <button type="button" class="btn" @click="closeInviteModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="inviting">
              {{ inviting ? 'Inviting…' : 'Invite Speaker' }}
            </button>
          </div>
        </form>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'SpeakerManager',
  data() {
    return {
      speakers: [],
      total: 0,
      page: 1,
      pageSize: 25,
      search: '',
      includeInactive: false,
      loading: false,
      error: null,
      showInvite: false,
      inviting: false,
      inviteError: null,
      inviteCredentials: null,
      copiedField: '',
      invite: this.blankInvite(),
      searchDebounce: null,
      metrics: null,
      // Org picker state (populated lazily via /users/speakers/target-orgs/).
      targetOrgs: [],
      recommendedOrgSlug: null,
      currentOrgSlug: null,
    }
  },
  computed: {
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    },
  },
  mounted() {
    // eslint-disable-next-line no-console
    console.log('[SpeakerManager] mounted')
    this.reload()
    this.loadMetrics()
  },
  methods: {
    blankInvite() {
      return {
        email: '',
        first_name: '',
        last_name: '',
        credentials: '',
        topicsRaw: '',
        headshot_url: '',
        cv_url: '',
        bio: '',
        send_email: true,
        // Empty = let backend default to the caller's current org. Org
        // picker (only rendered for platform admins with > 1 choice) sets
        // this to the recommended CE-publishing org.
        organization_slug: '',
      }
    },
    async loadTargetOrgs() {
      try {
        const res = await api.get('/users/speakers/target-orgs/')
        this.targetOrgs = res.data?.orgs || []
        this.recommendedOrgSlug = res.data?.recommended_slug || null
        this.currentOrgSlug = res.data?.current_slug || null
        // Default selection priority:
        //  1. recommended (the org actually publishing CE content)
        //  2. current org (single-org admins fall through here)
        //  3. first org in the list
        if (!this.invite.organization_slug) {
          this.invite.organization_slug =
            this.recommendedOrgSlug
            || this.currentOrgSlug
            || (this.targetOrgs[0] && this.targetOrgs[0].slug)
            || ''
        }
      } catch (e) {
        // Non-fatal — modal still works, backend will fall back to the
        // caller's own org. Log so the issue is debuggable.
        // eslint-disable-next-line no-console
        console.warn('[SpeakerManager] target-orgs lookup failed', e)
      }
    },
    initials(s) {
      const f = (s.first_name || '').slice(0, 1)
      const l = (s.last_name || '').slice(0, 1)
      return (f + l).toUpperCase() || (s.email || '?').slice(0, 1).toUpperCase()
    },
    debouncedReload() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.page = 1
        this.reload()
      }, 250)
    },
    async reload() {
      this.loading = true
      this.error = null
      try {
        const res = await api.get('/users/speakers/', {
          params: {
            q: this.search,
            include_inactive: this.includeInactive,
            page: this.page,
            page_size: this.pageSize,
          },
        })
        this.speakers = res.data.results || []
        this.total = res.data.count || 0
      } catch (e) {
        this.error = e?.response?.data?.detail || e.message || 'Failed to load speakers'
      } finally {
        this.loading = false
      }
    },
    async loadMetrics() {
      try {
        const res = await api.get('/users/speakers/metrics/')
        this.metrics = res.data
      } catch (e) {
        // Metrics tile is informational; never block the table on its failure.
        this.metrics = null
      }
    },
    goPage(p) {
      this.page = Math.max(1, Math.min(this.pageCount, p))
      this.reload()
    },
    openInviteModal() {
      this.invite = this.blankInvite()
      this.inviteError = null
      this.inviteCredentials = null
      this.copiedField = ''
      this.showInvite = true
      // Breadcrumb so "nothing happened when I clicked the button" never
      // turns into a silent debugging session again. console.log (not
      // console.debug) so it shows at Chrome's default level filter.
      // eslint-disable-next-line no-console
      console.log('[SpeakerManager] invite modal opened')
      // Fire-and-forget — modal is usable while orgs load. If it fails
      // we just don't render the picker and let the backend default to
      // the caller's current org.
      this.loadTargetOrgs()
    },
    closeInviteModal() {
      this.showInvite = false
      this.inviteCredentials = null
      this.copiedField = ''
    },
    async copyCredential(field, value) {
      try {
        await navigator.clipboard.writeText(value)
        this.copiedField = field
        setTimeout(() => {
          if (this.copiedField === field) this.copiedField = ''
        }, 1500)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[SpeakerManager] clipboard write failed', e)
      }
    },
    validateInvite() {
      const email = (this.invite.email || '').trim()
      if (!email) return 'Email is required.'
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return 'Please enter a valid email address.'
      }
      const urlOk = (v) => !v || /^https?:\/\/\S+/i.test(v)
      if (!urlOk(this.invite.headshot_url)) {
        return 'Headshot URL must start with http:// or https:// (leave blank if unsure).'
      }
      if (!urlOk(this.invite.cv_url)) {
        return 'CV URL must start with http:// or https:// (leave blank if unsure).'
      }
      return null
    },
    async submitInvite() {
      this.inviteError = null
      const validationError = this.validateInvite()
      if (validationError) {
        this.inviteError = validationError
        return
      }
      this.inviting = true
      try {
        const topics = (this.invite.topicsRaw || '')
          .split(/[,;|]/)
          .map((t) => t.trim())
          .filter(Boolean)
        const payload = {
          email: this.invite.email.trim().toLowerCase(),
          first_name: this.invite.first_name,
          last_name: this.invite.last_name,
          credentials: this.invite.credentials,
          headshot_url: this.invite.headshot_url,
          cv_url: this.invite.cv_url,
          bio: this.invite.bio,
          topics,
          send_email: this.invite.send_email,
        }
        // Backend only honors organization_slug for platform users, so it's
        // safe to always send it when the picker populated a value — non-
        // platform admins simply have it ignored.
        if (this.invite.organization_slug) {
          payload.organization_slug = this.invite.organization_slug
        }
        // eslint-disable-next-line no-console
        console.log('[SpeakerManager] submitting invite', { email: payload.email })
        const res = await api.post('/users/speakers/invite/', payload, { timeout: 15000 })
        // Avoid logging the response body — it may contain the cleartext
        // temp_password the backend returns on first creation.
        // eslint-disable-next-line no-console
        console.log(
          '[SpeakerManager] invite response',
          res.status,
          { created: res.data?.created, role_promoted: res.data?.role_promoted, email_sent: res.data?.email_sent },
        )
        if (res.data && res.data.warning) {
          this.inviteError = res.data.warning
          return
        }
        if (res.data && res.data.temp_password) {
          // Show the freshly-generated credentials so the admin can share
          // them out-of-band when SES is in sandbox mode or the speaker
          // can't find the invitation email.
          this.inviteCredentials = {
            email: res.data.email,
            password: res.data.temp_password,
            emailSent: !!res.data.email_sent,
          }
          await this.reload()
          this.loadMetrics()
          return
        }
        this.closeInviteModal()
        await this.reload()
        this.loadMetrics()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[SpeakerManager] invite failed', e?.response?.status, e?.response?.data || e)
        const data = e?.response?.data
        const fieldErrors = data && typeof data === 'object'
          ? Object.entries(data)
            .filter(([k]) => k !== 'detail')
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
            .join('; ')
          : ''
        this.inviteError = (
          data?.detail
          || fieldErrors
          || e.message
          || 'Failed to invite speaker'
        )
      } finally {
        this.inviting = false
      }
    },
  },
}
</script>

<style scoped>
.speaker-manager { padding: 16px; }
.header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 12px; }
.subtitle { color: #6b7280; font-size: 14px; margin: 4px 0 0 0; max-width: 540px; }
.toolbar { display: flex; gap: 12px; align-items: center; margin-bottom: 12px; }
.metrics-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin: 12px 0 16px; }
.metric { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px; padding: 12px 14px; }
.metric-label { font-size: 12px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.04em; }
.metric-value { font-size: 22px; font-weight: 700; color: #111827; margin: 4px 0; }
.metric-foot { font-size: 12px; color: #6B7280; }
.search-input { flex: 1; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; }
.toggle { display: inline-flex; gap: 6px; align-items: center; cursor: pointer; user-select: none; font-size: 14px; color: #4b5563; }
.loading, .error, .empty { padding: 32px; text-align: center; color: #6b7280; }
.error { color: #b91c1c; }
.speakers-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.speakers-table th { text-align: left; padding: 8px 12px; border-bottom: 1px solid #e5e7eb; color: #374151; font-weight: 600; }
.speakers-table td { padding: 12px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
.avatar-cell { width: 56px; }
.avatar-cell img { width: 40px; height: 40px; border-radius: 999px; object-fit: cover; }
.avatar-fallback { display: inline-flex; width: 40px; height: 40px; border-radius: 999px; background: #e5e7eb; color: #4b5563; align-items: center; justify-content: center; font-weight: 600; }
.name { font-weight: 600; color: #111827; }
.email { color: #6b7280; font-size: 12px; }
.topic-pill { display: inline-block; background: #ecfeff; color: #155e75; border-radius: 999px; padding: 2px 8px; font-size: 12px; margin-right: 4px; }
.muted { color: #9ca3af; }
.status-badge { padding: 2px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.status-badge.active { background: #d1fae5; color: #065f46; }
.status-badge.inactive { background: #f3f4f6; color: #6b7280; }
code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-size: 12px; }
.pagination { margin-top: 16px; display: flex; gap: 12px; align-items: center; justify-content: center; color: #6b7280; }
.pagination button { padding: 6px 12px; border: 1px solid #d1d5db; background: white; border-radius: 6px; cursor: pointer; }
.pagination button:disabled { opacity: 0.5; cursor: not-allowed; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 3000; }
.modal { background: white; border-radius: 12px; padding: 24px; width: 540px; max-width: calc(100vw - 32px); max-height: calc(100vh - 32px); overflow-y: auto; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25); }
.modal h3 { margin: 0 0 8px 0; }
.form-row { margin: 12px 0; display: flex; flex-direction: column; gap: 4px; }
.form-row.split { flex-direction: row; gap: 12px; }
.form-row.split > div { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.form-row label { font-size: 13px; color: #374151; font-weight: 500; }
.form-row input, .form-row textarea, .form-row select { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; font-family: inherit; background: white; }
.required { color: #b91c1c; }
.warning-inline { color: #b45309; font-weight: 500; margin-left: 4px; }
.modal-actions { margin-top: 16px; display: flex; gap: 8px; justify-content: flex-end; }
.btn { padding: 8px 16px; border-radius: 6px; border: 1px solid #d1d5db; background: white; cursor: pointer; font-size: 14px; }
.btn-primary { background: #2563eb; color: white; border-color: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.credentials-panel h3 { margin-top: 0; }
.cred-row { margin: 12px 0; display: flex; flex-direction: column; gap: 4px; }
.cred-row label { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.cred-value { display: flex; gap: 8px; align-items: center; }
.cred-value code { flex: 1; padding: 8px 12px; background: #f3f4f6; border-radius: 6px; font-size: 14px; word-break: break-all; user-select: all; }
.copy-btn { white-space: nowrap; }
.small { font-size: 12px; }
</style>
